/**
 *
 * useIntegrations hook
 *
 */

import React, { useCallback, useMemo, useState } from 'react';

import { AccountType } from '@ariksa/cloud-account';
import {
  CloudAccountApiOnboardCloudAccountRequest,
  CloudAccountApiUpdateCloudAccountRequest,
} from '@ariksa/cloud-account/api';
import { Clients } from '@ariksa/notification';
import { ClientResponse } from '@ariksa/notification/api';
import { Flex, useDisclosure } from '@chakra-ui/react';
import { map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { EditIcon, IconTypes, TrashIcon } from 'app/components/Icons';
import { SideNavMenuItemProps } from 'app/components/Navigation/SideNav';
import { DataSources } from 'app/containers/Setup/Integrations/Components/IntegrationsWizard/DataSources';
import { SIEM } from 'app/containers/Setup/Integrations/Components/IntegrationsWizard/SIEM';
import { SSO } from 'app/containers/Setup/Integrations/Components/IntegrationsWizard/SSO';
import { PageHeaderWithIcon } from 'components/DataDisplay';
import { ActionButton, AddButton, FormAction } from 'components/DataEntry';
import { Menu } from 'components/Overlay';
import { actions as appActions } from 'containers/App/slice';
import { IntegrationsTabs } from 'containers/Setup/Integrations/types';
import { selectSetup } from 'containers/Setup/selectors';
import { actions as setupActions } from 'containers/Setup/slice';
import { actions as sharedStateActions } from 'containers/SharedState/slice';

import { EmailGroup, Jira, ServiceNow, Slack, SMS, Teams } from '../Components';
import { actions } from '../slice';

import { getLabel, getResourceIcon, renderNavTitle } from './utils';

export const useIntegrations = () => {
  const dispatch = useDispatch();
  const { accounts } = useSelector(selectSetup);
  const [actionType, setActionType] = useState<FormAction>('Add');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const { isOpen, onOpen } = useDisclosure();
  const {
    isOpen: isOpenRediscoverModal,
    onClose: onCloseRediscoverModal,
    onOpen: onOpenRediscoverModal,
  } = useDisclosure();
  const deleteClient = useDisclosure();
  const navigate = useNavigate();
  const params = useParams<{
    provider: string;
    id: string;
  }>();

  const onCancel = useCallback(() => {
    navigate('/setup/integrations/' + params.provider);
  }, [navigate, params.provider]);

  const getAccounts = useCallback(
    (accountType: AccountType) => {
      dispatch(
        setupActions.getAccounts({
          q: {
            page: accounts.page.info.page_number,
            size: accounts.page.info.page_size,
            accountType: accountType,
            cloudType: params?.provider,
          },
          onSuccess: res => {
            !!res?.total &&
              dispatch(
                sharedStateActions.getSnapshotStatus({
                  q: {
                    getLatestWorkflowRequest: {
                      account_ids: map(res?.items, o => o.uuid),
                    },
                  },
                }),
              );
          },
        }),
      );
    },
    [dispatch, accounts.page.info, params?.provider],
  );

  const onConfirmDeleteClient = useCallback(
    (uuid, clientName) => {
      dispatch(
        actions.deleteClient({
          q: { uuid },
          onSuccess: () => {
            deleteClient.onClose();
            onCancel();
          },
        }),
      );
    },
    [dispatch, deleteClient, onCancel],
  );
  const onConfirmDeleteAccount = useCallback(
    (uuid: string, accountType: AccountType) => {
      dispatch(
        actions.deleteAccount({
          q: { uuid },
          onSuccess: () => {
            getAccounts(accountType);
            appActions.loadEnvAndAccounts();
          },
        }),
      );
    },
    [dispatch, getAccounts],
  );

  const onEditProvider = useCallback(
    (row: Record<string, any>) => {
      navigate(`/setup/integrations/edit/${row.cloud_type}/${row.uuid}`);
    },
    [navigate],
  );

  const onClickRediscoverInventory = useCallback(
    (row: Record<string, any>) => {
      onOpenRediscoverModal();
      setCurrentRecord(row);
    },
    [onOpenRediscoverModal],
  );

  const onConfirmRediscoverInventory = useCallback(
    (accountType: AccountType) => {
      dispatch(
        actions.rediscoverInventory({
          q: { uuid: currentRecord.uuid },
          onSuccess: () => {
            onCloseRediscoverModal();
            getAccounts(accountType);
          },
        }),
      );
    },
    [dispatch, currentRecord, onCloseRediscoverModal, getAccounts],
  );

  const onEditClient = useCallback(
    (row: ClientResponse) => {
      navigate(`/setup/integrations/edit/${row.client_name}/${row.uuid}`);
    },
    [navigate],
  );

  const updateAccount = useCallback(
    (
      payload: CloudAccountApiUpdateCloudAccountRequest,
      accountType: AccountType,
    ) => {
      dispatch(
        actions.updateAccount({
          q: payload,
          onSuccess: () => {
            onCancel();
          },
        }),
      );
    },
    [dispatch, onCancel],
  );

  const onboardAccount = useCallback(
    (
      payload: CloudAccountApiOnboardCloudAccountRequest,
      accountType: AccountType,
    ) => {
      dispatch(
        setupActions.onboardAccount({
          q: payload,
          onSuccess: () => {
            onCancel();
          },
        }),
      );
    },
    [dispatch, onCancel],
  );

  const onAddClient = useCallback(() => {
    onOpen();
    setActionType('Add');
    dispatch(actions.resetActiveClient());
  }, [onOpen, dispatch]);

  const onClickAddProvider = useCallback(
    (value: string) => {
      setSelectedProvider(value);
      onOpen();
      setActionType('Add');
    },
    [onOpen],
  );

  const renderContentHeader = useCallback(
    (type: string, iconFilled?: boolean) => {
      return (
        <PageHeaderWithIcon
          label={(isOpen ? actionType + ' ' : '') + getLabel(type)}
          iconType={type}
          iconFilled={iconFilled}
          reversed={isOpen}
        />
      );
    },
    [actionType, isOpen],
  );

  const siemOptions = useMemo(
    () => [
      {
        key: IntegrationsTabs.Elasticsearch,
        label: 'Elastic For SIEM',
        icon: getResourceIcon(Clients.Elasticsearch),
        onClick: () => onClickAddProvider(Clients.Elasticsearch),
      },
    ],
    [onClickAddProvider],
  );

  const dataSourceOptions = useMemo(
    () => [
      {
        key: IntegrationsTabs.Gitlab,
        label: 'GitLab',
        icon: getResourceIcon(IntegrationsTabs.Gitlab),
        onClick: () => onClickAddProvider(IntegrationsTabs.Gitlab),
      },
      {
        key: IntegrationsTabs.Github,
        label: 'GitHub',
        icon: getResourceIcon(IntegrationsTabs.Github),
        onClick: () => onClickAddProvider(IntegrationsTabs.Github),
      },
      {
        key: IntegrationsTabs.Bitbucket,
        label: 'Atlassian BitBucket',
        icon: getResourceIcon(IntegrationsTabs.Bitbucket),
        onClick: () => onClickAddProvider(IntegrationsTabs.Bitbucket),
      },
      {
        key: IntegrationsTabs.Tableau,
        label: 'Tableau',
        icon: getResourceIcon(IntegrationsTabs.Tableau),
        onClick: () => onClickAddProvider(IntegrationsTabs.Tableau),
      },

      /*{
        key: IconTypes.GoogleDrive,
        label: 'Google Drive',
        icon: getResourceIcon(IconTypes.GoogleDrive),
        onClick: () => onClickAddProvider(CloudProviders.Bitbucket),
      },
      {
        key: IconTypes.MicrosoftOneDrive,
        label: 'Microsoft OneDrive',
        icon: getResourceIcon(IconTypes.MicrosoftOneDrive),
        onClick: () => onClickAddProvider(CloudProviders.Bitbucket),
      },
      {
        key: IconTypes.Box,
        label: 'Box',
        icon: getResourceIcon(IconTypes.Box),
        onClick: () => onClickAddProvider(CloudProviders.Bitbucket),
      },
      {
        key: IconTypes.Dropbox,
        label: 'Dropbox',
        icon: getResourceIcon(IconTypes.Dropbox),
        onClick: () => onClickAddProvider(CloudProviders.Bitbucket),
      },*/
    ],
    [onClickAddProvider],
  );

  const integrationsMenuItems: SideNavMenuItemProps[] = useMemo(
    () => [
      {
        title: 'SaaS',
        key: IntegrationsTabs.Saas,
        icon: getResourceIcon(IconTypes.EbsSnapshot),
        component: <DataSources />,
        rightContentHeader: !isOpen && (
          <Menu
            menuItems={dataSourceOptions}
            buttonLabel={<AddButton label="Add SaaS" onClick={onAddClient} />}
          />
        ),
        contentHeader: isOpen
          ? renderContentHeader(selectedProvider, false)
          : renderContentHeader(IntegrationsTabs.Saas, true),
      },
      {
        title: 'Ticketing / ITSM',
        key: IntegrationsTabs.Ticketing,
        icon: getResourceIcon(IconTypes.Ticket),
        secondary: [
          {
            title: renderNavTitle(IconTypes.Servicenow),
            key: IntegrationsTabs.Servicenow,
            component: <ServiceNow />,
            contentHeader: renderContentHeader(
              IntegrationsTabs.Servicenow,
              false,
            ),
            rightContentHeader: !isOpen && (
              <AddButton label="Add Client" onClick={onAddClient} />
            ),
          },
          {
            title: renderNavTitle(IconTypes.Jira),
            key: IntegrationsTabs.Jira,
            component: <Jira />,
            contentHeader: renderContentHeader(IntegrationsTabs.Jira, false),
            rightContentHeader: !isOpen && (
              <AddButton label="Add Project" onClick={onAddClient} />
            ),
          },
        ],
      },
      {
        title: 'Notification',
        key: IntegrationsTabs.Notification,
        icon: getResourceIcon(IconTypes.Notification),
        secondary: [
          {
            title: renderNavTitle(IconTypes.Slack),
            key: IntegrationsTabs.Slack,
            component: <Slack />,
            contentHeader: renderContentHeader(IconTypes.Slack, false),
            rightContentHeader: !isOpen && (
              <AddButton label="Add Channel" onClick={onAddClient} />
            ),
          },
          {
            title: renderNavTitle(IconTypes.Teams),
            key: IntegrationsTabs.Teams,
            component: <Teams />,
            contentHeader: renderContentHeader(IconTypes.Teams, false),
            rightContentHeader: !isOpen && (
              <AddButton label="Add Channel" onClick={onAddClient} />
            ),
          },
          {
            title: renderNavTitle(IconTypes.Email),
            key: IntegrationsTabs.Email,
            component: <EmailGroup />,
            contentHeader: renderContentHeader(IconTypes.Email),
            rightContentHeader: !isOpen && (
              <AddButton label="Add Group" onClick={onAddClient} />
            ),
          },
          {
            title: renderNavTitle(IconTypes.Sms),
            key: IntegrationsTabs.Sms,
            component: <SMS />,
            contentHeader: renderContentHeader(IconTypes.Sms),
            rightContentHeader: !isOpen && (
              <AddButton label="Add SMS" onClick={onAddClient} />
            ),
          },
        ],
      },
      {
        title: 'Single Sign-On',
        key: IntegrationsTabs.Sso,
        component: <SSO />,
        icon: getResourceIcon(IconTypes.Sso),
        contentHeader: renderContentHeader(IconTypes.Sso),
        rightContentHeader: !isOpen && (
          <AddButton label="Add Provider" onClick={onAddClient} />
        ),
      },
      {
        title: 'SIEM',
        key: IntegrationsTabs.Siem,
        icon: getResourceIcon(IconTypes.SIEM),
        rightContentHeader: !isOpen && (
          <Menu
            menuItems={siemOptions}
            buttonLabel={<AddButton label="Add SIEM" onClick={onAddClient} />}
          />
        ),
        contentHeader: isOpen
          ? renderContentHeader(selectedProvider, false)
          : renderContentHeader(IconTypes.SIEM),
        component: <SIEM />,
      },
    ],
    [
      isOpen,
      onAddClient,
      siemOptions,
      selectedProvider,
      dataSourceOptions,
      renderContentHeader,
    ],
  );

  const clientActionsColumn = {
    header: 'Actions',
    accessor: '',
    align: 'center',
    styles: { cell: { overflow: 'visible' } },
    render: ({ row }) => {
      return (
        <Flex justifyContent="center">
          <ActionButton
            label={'Edit'}
            icon={<EditIcon />}
            onClick={() => {
              navigate(
                `/setup/integrations/edit/${row.client_name}/${row.uuid}`,
              );
            }}
          />
          <ActionButton
            label={'Delete'}
            icon={<TrashIcon color="red" />}
            onClick={() => {
              deleteClient.onOpen();
              setCurrentRecord(row);
            }}
          />
        </Flex>
      );
    },
  };

  return {
    onConfirmDeleteClient,
    onConfirmDeleteAccount,
    onEdit: onEditClient,
    onEditProvider,
    onAddClient,
    actionType,
    isOpen,
    onCancel,
    setActionType,
    selectedProvider,
    updateAccount,
    onboardAccount,
    integrationsMenuItems,
    onClickAddProvider,
    currentRecord,
    onClickRediscoverInventory,
    onConfirmRediscoverInventory,
    onCloseRediscoverModal,
    isOpenRediscoverModal,
    getAccounts,
    isOpenDeleteClientModal: deleteClient.isOpen,
    onCloseDeleteClientModal: deleteClient.onClose,
    clientActionsColumn,
    setCurrentRecord,
  };
};
